/**
 * CALIBRATE — the guided flow that measures a stick instead of guessing at it,
 * and the reason the CONTROLS panel has no list of axis numbers to pick from.
 *
 * ## Why it asks for movements rather than for indices
 *
 * "Which axis is the rudder?" is a question a player cannot answer and the
 * browser will not. The device reports a bare array of numbers whose order is
 * whatever its descriptor says, and the honest way to find out which is which
 * is the way every flight sim since the 1980s has: ask for a movement and watch
 * which number moves. That also settles the two things an index alone could
 * never carry — **which way round it is** (the peak's sign against the rest
 * position) and **how far it actually travels** (a stick whose limit reads 0.86
 * must still give full aileron).
 *
 * A step whose largest movement is under `CALIBRATION_MIN_TRAVEL` is **skipped,
 * not failed**: that is how a two-axis stick with no twist grip gets past the
 * rudder step and how a stick with no quadrant gets past the throttle, leaving
 * those roles unmapped so the keyboard keeps them.
 *
 * ## The centre step also finds the hat, and it has to
 *
 * A DirectInput hat is a single axis carrying eight detents, and at rest it
 * reads **outside** ±1 — 3.2857 on the encoding every driver here has used. So
 * one scan of the rest positions identifies it for nothing.
 *
 * That is not a convenience, it is a correctness requirement. Left unclaimed,
 * a hat axis swings from 3.2857 to −1 when it is pressed: a movement of over
 * four, which is four times what a real control axis can produce. Any player
 * who nudged the hat during the elevator step would have his hat bound to the
 * elevator, and nothing else in the flow could out-vote it.
 *
 * Pure — a step is a function of the previous step and an array of numbers.
 */

import {
  AXIS_ROLES,
  NO_HAT,
  PAD_ACTIONS,
  UNMAPPED,
  type AxisMapping,
  type AxisRole,
  type GamepadMapping,
  type HatSource,
  type PadAction,
} from "./gamepad-mapping.ts";

/**
 * The flow, in order. The throttle takes two steps because it is the one axis
 * with no rest position to measure against — a quadrant sits wherever it was
 * left, so both ends have to be asked for.
 */
export const CALIBRATION_STEPS = ["centre", "pitch", "roll", "yaw", "throttleIdle", "throttleOpen", "done"] as const;
export type CalibrationStep = (typeof CALIBRATION_STEPS)[number];

/**
 * Printed on the panel, one line each. Every prompt names the **positive**
 * sense the sim uses, so the sign of what is measured is the sign that is
 * wanted: back stick is +1 pitch, right bank is +1 roll, right rudder is +1
 * yaw (`packages/engine`'s `inputs.ts`).
 */
export const CALIBRATION_PROMPTS: Readonly<Record<CalibrationStep, string>> = {
  centre: "LET GO OF EVERYTHING, THEN CONFIRM",
  pitch: "PULL THE STICK FULLY BACK",
  roll: "BANK FULLY TO THE RIGHT",
  yaw: "FULL RIGHT RUDDER - OR SKIP",
  throttleIdle: "CLOSE THE THROTTLE - OR SKIP",
  throttleOpen: "OPEN THE THROTTLE FULLY",
  done: "CALIBRATED - ACCEPT TO KEEP IT",
};

/**
 * How far an axis must move for a step to bind it. A deliberate deflection
 * moves an axis by about 1.0 in raw units; a hand resting on a stick, or a worn
 * potentiometer's drift, moves it by hundredths. A third of full travel sits
 * clear of both and is low enough that a stiff or short-throw axis still counts.
 */
export const CALIBRATION_MIN_TRAVEL = 0.35;

/** Anything reading outside this at rest is not a control axis (see the header). */
const HAT_REST_THRESHOLD = 1.05;

/** Steps that bind one of the three centred control axes, and which role each binds. */
const AXIS_STEP_ROLES: Partial<Record<CalibrationStep, AxisRole>> = { pitch: "pitch", roll: "roll", yaw: "yaw" };

export interface CalibrationState {
  readonly step: CalibrationStep;
  /** Every axis's reading at the `centre` step — the reference the three control axes are measured from. */
  readonly rest: readonly number[];
  /** Every axis's reading at `throttleIdle`, which the throttle is measured from instead. */
  readonly idle: readonly number[];
  /** Per axis, the furthest-from-reference value this step has seen. */
  readonly peaks: readonly number[];
  /** What has been bound so far. A role missing here is a role the flow skipped. */
  readonly axes: Readonly<Partial<Record<AxisRole, AxisMapping>>>;
  /** The hat, if the centre step found one on an axis. */
  readonly hat: HatSource | undefined;
  /** Axis indices already spoken for, so one axis cannot be bound to two roles. */
  readonly taken: readonly number[];
}

export function beginCalibration(axisCount: number): CalibrationState {
  const zeros: readonly number[] = Array.from({ length: Math.max(axisCount, 0) }, () => 0);
  return { step: "centre", rest: zeros, idle: zeros, peaks: zeros, axes: {}, hat: undefined, taken: [] };
}

/** Which readings this step measures movement against — the panel prints movement against the same baseline. */
export function calibrationReference(state: CalibrationState): readonly number[] {
  return state.step === "throttleOpen" ? state.idle : state.rest;
}

const referenceFor = calibrationReference;

/**
 * One poll during a step: keeps, per axis, whichever reading is furthest from
 * that axis's reference. Peaks rather than the live value, so a player can push
 * the stick to its stop and relax before confirming — which is what a hand
 * actually does.
 */
export function sampleCalibration(state: CalibrationState, axes: readonly number[]): CalibrationState {
  if (state.step === "centre" || state.step === "throttleIdle" || state.step === "done") return state;
  const reference = referenceFor(state);
  let changed = false;
  const peaks = state.peaks.map((peak, index) => {
    const live = axes[index];
    if (live === undefined || !Number.isFinite(live)) return peak;
    const base = reference[index] ?? 0;
    if (Math.abs(live - base) <= Math.abs(peak - base)) return peak;
    changed = true;
    return live;
  });
  return changed ? { ...state, peaks } : state;
}

/** The axis that moved furthest during this step, ignoring ones already bound. */
function widestMovement(state: CalibrationState): { index: number; peak: number; travel: number } | undefined {
  const reference = referenceFor(state);
  let best: { index: number; peak: number; travel: number } | undefined;
  for (let index = 0; index < state.peaks.length; index++) {
    if (state.taken.includes(index)) continue;
    const peak = state.peaks[index] ?? 0;
    const travel = Math.abs(peak - (reference[index] ?? 0));
    if (travel < CALIBRATION_MIN_TRAVEL) continue;
    if (!best || travel > best.travel) best = { index, peak, travel };
  }
  return best;
}

/** Every axis sitting outside the control range at rest — a hat, on the DirectInput encoding. */
function hatAxisIn(rest: readonly number[]): number | undefined {
  for (let index = 0; index < rest.length; index++) {
    const value = rest[index];
    if (value !== undefined && Number.isFinite(value) && Math.abs(value) > HAT_REST_THRESHOLD) return index;
  }
  return undefined;
}

function nextStep(step: CalibrationStep): CalibrationStep {
  const at = CALIBRATION_STEPS.indexOf(step);
  return CALIBRATION_STEPS[Math.min(at + 1, CALIBRATION_STEPS.length - 1)] ?? "done";
}

/** Restarts the peak tracking for the step being entered — a previous step's extremes mean nothing to the next one. */
function advanced(state: CalibrationState, patch: Partial<CalibrationState>): CalibrationState {
  const step = nextStep(state.step);
  const merged = { ...state, ...patch, step };
  return { ...merged, peaks: (patch.rest ?? patch.idle ?? merged.rest).slice() };
}

function commitCentre(state: CalibrationState, axes: readonly number[]): CalibrationState {
  const rest = state.peaks.map((_, index) => (Number.isFinite(axes[index]) ? (axes[index] as number) : 0));
  const hatIndex = hatAxisIn(rest);
  return advanced(state, {
    rest,
    idle: rest,
    ...(hatIndex === undefined ? {} : { hat: { kind: "axis", index: hatIndex } as HatSource, taken: [...state.taken, hatIndex] }),
  });
}

function commitAxis(state: CalibrationState, role: AxisRole): CalibrationState {
  const widest = widestMovement(state);
  // Nothing moved far enough: the step is skipped and the role stays unmapped,
  // which is how a stick with no rudder gets past the rudder step.
  if (!widest) return advanced(state, {});
  const base = referenceFor(state)[widest.index] ?? 0;
  const mapping: AxisMapping = { index: widest.index, invert: widest.peak < base, centre: base, travel: widest.travel };
  return advanced(state, { axes: { ...state.axes, [role]: mapping }, taken: [...state.taken, widest.index] });
}

/**
 * Confirms the step on screen and moves to the next. `axes` is the live
 * reading, which only the two "hold it still" steps use — everywhere else the
 * answer is the peak the step has been accumulating.
 */
export function confirmCalibration(state: CalibrationState, axes: readonly number[]): CalibrationState {
  if (state.step === "done") return state;
  if (state.step === "centre") return commitCentre(state, axes);
  if (state.step === "throttleIdle") {
    const idle = state.rest.map((rest, index) => (Number.isFinite(axes[index]) ? (axes[index] as number) : rest));
    return advanced(state, { idle });
  }
  if (state.step === "throttleOpen") return commitAxis(state, "throttle");
  const role = AXIS_STEP_ROLES[state.step];
  return role ? commitAxis(state, role) : advanced(state, {});
}

/** Skips the step outright, leaving its role unmapped. What the panel's SKIP button does. */
export function skipCalibrationStep(state: CalibrationState): CalibrationState {
  if (state.step === "done") return state;
  // A skipped centre step would leave every later step measuring against
  // nothing, so it takes the live-reading path rather than being skippable.
  return advanced(state, {});
}

/**
 * The measured mapping, folded onto whatever the device already had. Buttons
 * and — on a standard controller — the D-pad hat are untouched: this flow
 * measures axes, and a button is bound by pressing it (`learnButton`).
 */
export function calibratedMapping(state: CalibrationState, base: GamepadMapping): GamepadMapping {
  const axes = Object.fromEntries(
    AXIS_ROLES.map((role) => [role, state.axes[role] ?? { index: UNMAPPED, invert: false, centre: 0, travel: 1 }]),
  ) as Record<AxisRole, AxisMapping>;
  return { ...base, axes, hat: state.hat ?? (base.hat.kind === "buttons" ? base.hat : NO_HAT) };
}

/**
 * Binds one button to one action.
 *
 * The button is cleared off every *other* action first, so two jobs can never
 * share one button — which would leave a player pulling the trigger and opening
 * PREFERENCES, with no way to see from the panel that he had done it.
 */
export function learnButton(mapping: GamepadMapping, action: PadAction, button: number): GamepadMapping {
  const buttons = Object.fromEntries(
    PAD_ACTIONS.map((name) => [name, name === action ? button : mapping.buttons[name] === button ? UNMAPPED : mapping.buttons[name]]),
  ) as Record<PadAction, number>;
  return { ...mapping, buttons };
}

/** Unbinds one action, leaving its button free. */
export function clearButton(mapping: GamepadMapping, action: PadAction): GamepadMapping {
  return { ...mapping, buttons: { ...mapping.buttons, [action]: UNMAPPED } };
}
